// User persona types
export type PersonaType =
  | 'internal_admin'
  | 'internal_user'
  | 'coach'
  | 'participant'
  | 'partner';

export interface UserPersona {
  id: string;
  persona: PersonaType;
  permissions: {
    can_create: boolean;
    can_read: boolean;
    can_update: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
  is_primary?: boolean;
}

// User types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  avatar_url?: string;
  initials: string;
  display_name: string;
  personas: UserPersona[];
  primary_persona?: PersonaType;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  coach_type?: 'medley' | 'partner';
  partner_id?: string | null;
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  tokens: TokenPair;
}

export interface RefreshResponse {
  tokens: TokenPair;
}

// API response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// Pagination
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Partner types
export interface Partner {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  website: string | null;
  logo_url: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  is_active: boolean;
  program_count: number;
  cohort_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CreatePartnerRequest {
  name: string;
  code: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  description?: string;
  website?: string;
}

export interface UpdatePartnerRequest {
  name?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  description?: string;
  website?: string;
  is_active?: boolean;
}

export interface PartnerDeletionImpact {
  active_cohort_count: number;
  partner_coach_count: number;
}

// Program types
export interface Program {
  id: string;
  partner_id: string;
  name: string;
  description: string | null;
  partner_name: string;
  partner_code: string | null;
  cohort_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateProgramRequest {
  partner_id: string;
  name: string;
  description?: string;
}

export interface UpdateProgramRequest {
  name?: string;
  description?: string;
  is_active?: boolean;
}

// Cohort types
// MED-160: Status progression is Draft → In Design → In Prep → Live → Wrap Up → Completed
export type CohortStatus =
  | 'draft'
  | 'in_design'
  | 'in_prep'
  | 'live'
  | 'wrap_up'
  | 'completed'
  | 'cancelled'
  | 'archived';

export type CohortType = 'group_coaching';

export type SessionFrequency = 'weekly' | 'biweekly' | 'monthly';

export type PreferredDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri';

export type PreferredTime = 'morning' | 'afternoon' | 'evening';

// Modality types
export type ModalityType = 'group_coaching' | 'one_on_one' | 'workshop';

export type SessionType =
  | 'group_session'
  | 'one_on_one_session'
  | 'non_coaching_one_on_one'
  | 'workshop_session';

export type ParticipantAssignment = 'all_participants' | 'subset';

export type WorkshopPattern = 'parallel' | 'sequential';

// MED-848 — group_coaching sub-type. Only meaningful for group_coaching modalities.
export type CoachingSubType = 'coach_led' | 'peer_led';

// MED-689 E22-5a — 1:1 coaching session types
export const ONE_ON_ONE_TYPES = [
  'standard_1_1',
  'two_on_one',
  '360_debrief',
  'hogan_debrief',
  'other_assessment_debrief',
] as const;

// MED-1350: intro and midpoint check-ins are 1:1 rows with these types.
// They are never in session_configs and never paid.
export const CHECKIN_ONE_ON_ONE_TYPES = ['intro_checkin', 'midpoint_checkin'] as const;
// MED-1350: every check-in call is 20 minutes long. The participant books the
// coach's 20 min Calendly event, and the Edit Session modal fills the end time
// with this length.
export const CHECKIN_DURATION_MINUTES = 20;
export type ConfigurableOneOnOneType = (typeof ONE_ON_ONE_TYPES)[number];
export type CheckinOneOnOneType = (typeof CHECKIN_ONE_ON_ONE_TYPES)[number];
export function isCheckinOneOnOneType(
  type: string | null | undefined,
): type is CheckinOneOnOneType {
  return type === 'intro_checkin' || type === 'midpoint_checkin';
}

export type OneOnOneType = ConfigurableOneOnOneType | CheckinOneOnOneType;

export const ONE_ON_ONE_TYPE_LABELS: Record<OneOnOneType, string> = {
  standard_1_1: 'Standard 1:1',
  two_on_one: '2:1 (with Manager)',
  '360_debrief': '360 Debrief',
  hogan_debrief: 'Hogan Debrief',
  other_assessment_debrief: 'Other Assessment Debrief',
  intro_checkin: 'Intro',
  midpoint_checkin: 'Midpoint',
};

export const DEFAULT_ONE_ON_ONE_TYPE: ConfigurableOneOnOneType = 'standard_1_1';

export interface OneOnOneSessionConfig {
  session_number: number;
  duration_minutes: number;
  one_on_one_type: ConfigurableOneOnOneType;
  // MED-1350: program week of the 1:1, from the wizard's week date.
  target_week?: number | null;
}

export interface ModalityConfiguration {
  pattern?: WorkshopPattern;
  session_configs?: OneOnOneSessionConfig[];
  [key: string]: unknown;
}

export interface WorkshopDefinition {
  id: string;
  modality_id: string;
  title: string;
  sequence_number: number | null;
  participant_assignment: ParticipantAssignment;
  facilitator_id: string | null;
  facilitator_name?: string;
  facilitator_email?: string;
  attendance_tracking_enabled: boolean;
  session_duration_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export interface Modality {
  id: string;
  cohort_id: string;
  modality_type: ModalityType;
  session_count: number | null;
  session_frequency: SessionFrequency | null;
  session_duration_minutes: number | null;
  configuration: ModalityConfiguration;
  sort_order: number;
  announced_at: string | null;
  announced_by: string | null;
  invites_sent: boolean;
  // MED-848
  coaching_sub_type: CoachingSubType | null;
  ai_features_enabled: boolean;
  // MED-948 (E1.10a) — per-modality Live Session Delivery gate; defaults false.
  live_session_delivery_enabled: boolean;
  // MED-1069 (E1.10c) — per-modality Commitment Tracking (group_coaching only).
  // Relocated from cohort level (E1.10b). Independent of Live Session Delivery.
  commitment_tracking_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ModalityWithWorkshops extends Modality {
  workshops: WorkshopDefinition[];
  // MED-955 — cohort-wide derived boolean: true if ANY session in this
  // modality's cohort has a designated leader. Drives the Edit Cohort lock
  // on coaching_sub_type. Same value across every modality of the cohort.
  session_leaders_designated: boolean;
}

export interface WorkshopInput {
  title: string;
  sequence_number?: number;
  participant_assignment?: ParticipantAssignment;
  facilitator_id?: string | null;
  attendance_tracking_enabled?: boolean;
  session_duration_minutes?: number;
}

export interface CreateModalityInput {
  modality_type: ModalityType;
  session_count?: number;
  session_frequency?: SessionFrequency;
  session_duration_minutes?: number;
  configuration?: ModalityConfiguration;
  workshops?: WorkshopInput[];
  // MED-848 — required for group_coaching, omitted/null otherwise.
  coaching_sub_type?: CoachingSubType | null;
  ai_features_enabled?: boolean;
  // MED-948 (E1.10a) — per-modality Live Session Delivery gate; defaults false.
  live_session_delivery_enabled?: boolean;
  // MED-1069 (E1.10c) — per-modality Commitment Tracking; defaults false,
  // independent of Live Session Delivery (group_coaching only).
  commitment_tracking_enabled?: boolean;
}

export type CohortTeamRole =
  | 'delivery_program_manager'
  | 'delivery_program_associate'
  | 'head_of_delivery'
  | 'head_of_coaching'
  | 'head_of_programs'
  | 'head_of_curriculum'
  | 'head_of_sales'
  | 'sales_associate'
  | 'head_of_renewals';

export type MeetingPlatform = 'zoom' | 'teams' | 'google_meet' | 'in_person';

// MED-505: Compensation types
export type AudienceType = 'emerging' | 'established' | 'executive';

export const AUDIENCE_TYPE_LABELS: Record<AudienceType, string> = {
  emerging: 'Emerging Leaders',
  established: 'Established Leaders',
  executive: 'Executive Leaders',
};

/** Tiered rates: audience_type → duration_minutes → rate_cents */
export type TieredRates = Record<AudienceType, Record<string, number>>;

/**
 * Flat rate activity keys.
 * MED-910 / E13-8: `cohort_event_per_hour` split into independent
 * `orientation` + `wrap_up` rates. The legacy key is kept (deprecated,
 * read-only) because confirmed rate_snapshots still reference it.
 */
export type FlatRateKey =
  | 'huddle'
  | 'immersion'
  | 'orientation'
  | 'wrap_up'
  /** @deprecated MED-910 — split into `orientation` + `wrap_up`. */
  | 'cohort_event_per_hour'
  | 'coach_debrief'
  | 'workshop'
  | 'in_person_half_day'
  | 'in_person_full_day';

/** Flat rates: activity_key → rate_cents */
export type FlatRates = Record<FlatRateKey, number>;

/** MED-693: Session-type keys under one_on_one_sda (match v161 enum values). */
export const ONE_ON_ONE_SDA_TYPES = [
  'standard_1_1',
  'two_on_one',
  '360_debrief',
  'hogan_debrief',
  'other_assessment_debrief',
] as const;
export type OneOnOneSdaType = (typeof ONE_ON_ONE_SDA_TYPES)[number];

/** MED-693: Display labels for each one_on_one_sda session type */
export const ONE_ON_ONE_SDA_LABELS: Record<OneOnOneSdaType, string> = {
  standard_1_1: 'Standard 1:1 SDA',
  two_on_one: '2:1 with Manager SDA',
  '360_debrief': '360 Debrief SDA',
  hogan_debrief: 'Hogan Debrief SDA',
  other_assessment_debrief: 'Other Assessment Debrief SDA',
};

/** MED-693: 1:1 SDA tables keyed by session type */
export type OneOnOneSdaRates = Record<OneOnOneSdaType, TieredRates>;

/** Full rate card JSONB structure (MED-693) */
export interface RateCardRates {
  group_session: TieredRates;
  one_on_one_sda: OneOnOneSdaRates;
  flat: FlatRates;
}

/** Database row: rate_cards table */
export interface RateCard {
  id: string;
  rates: RateCardRates;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Activity log change item */
export interface RateCardChange {
  category: string;
  /** MED-693: one_on_one_sda session type (standard_1_1, 360_debrief, …) */
  session_type?: string;
  audience_type?: string;
  duration?: string;
  flat_key?: string;
  old_value: number;
  new_value: number;
}

/** Activity log entry with user name */
export interface RateCardActivityLogEntry {
  id: string;
  rate_card_id: string;
  performed_by: string;
  performed_by_name: string;
  action: string;
  description: string;
  changes: RateCardChange[];
  created_at: string;
}

/** Per-cohort compensation config */
export interface CohortCompensationConfig {
  id: string;
  cohort_id: string;
  audience_type: AudienceType;
  rate_overrides: Partial<RateCardRates>;
  rate_snapshot: RateCardRates | null;
  is_confirmed: boolean;
  confirmed_at: string | null;
  confirmed_by: string | null;
  created_at: string;
  updated_at: string;
  /** MED-910: cohort-level excluded component keys (AC1–AC7). */
  excluded_components: string[];
  /** MED-910 AC0j: configurable expected huddle count (default 2, min 0). */
  huddle_count: number;
}

/**
 * MED-910 AC0m–AC0q: read-only cohort-config values that feed the calc,
 * shown in the modal's "Calculation Inputs" section. null ⇒ "Not set yet".
 */
export interface CompensationCalculationInputs {
  group_coaching_session_count: number | null;
  group_coaching_duration_minutes: number | null;
  workshop_count: number | null;
  one_on_one_session_count: number | null;
  one_on_one_duration_minutes: number | null;
  target_group_size: number | null;
  program_months: number | null;
}

/** GET /api/cohorts/:id/compensation response */
export interface CohortCompensationResponse {
  config: CohortCompensationConfig | null;
  resolved_rates: RateCardRates | null;
  cohort_modalities: string[];
  session_duration_minutes: number | null;
  /** MED-910 AC0m–AC0q: read-only inputs for the Calculation Inputs section. */
  calculation_inputs: CompensationCalculationInputs;
  /**
   * MED-731: 1:1 session types actually configured on the cohort. Drives which
   * 1:1 SDA rows are rendered in the Configure Compensation modal. When the
   * cohort only uses standard_1_1 sessions, every other session-type row is
   * hidden.
   */
  configured_one_on_one_session_types: OneOnOneSdaType[];
  /**
   * MED-731: 1:1 session durations (in minutes) actually configured on the
   * cohort. Unused durations (e.g. 45-min when only 60-min is used) are
   * hidden in the modal.
   */
  configured_one_on_one_durations: number[];
  /**
   * MED-731: True when the cohort has at least one in_person_booster event.
   * Gates the In-Person Half/Full Day rate rows.
   */
  has_in_person_events: boolean;
}

// ============================================================
// Compensation Calculation Results (MED-507 / E13-3)
// ============================================================

export type CompensationCalcStatus = 'system_calculated' | 'pending' | 'confirmed';

/** Line item from coach_compensation_line_items table */
export interface CompensationLineItem {
  id: string;
  result_id: string;
  category: 'standard' | 'add_on';
  item_key: string;
  label: string;
  payment_track: 'flat_rate_monthly' | 'attendance_dependent_net_10';
  quantity: number | null;
  unit_rate_cents: number | null;
  total_cents: number;
  adjusted_total_cents: number | null;
  adjusted_by: string | null;
  adjusted_at: string | null;
  is_potential: boolean;
  is_rate_overridden: boolean;
  metadata: Record<string, unknown>;
  /**
   * MED-677: Computed server-side from LINE_ITEM_TO_MODALITY + the coach's
   * cohort_coaches.confirmed_modalities. True when this line item is tied to
   * one of the coach's actual assignments. Used post-announcement by the
   * admin view to render "N/A" for unassigned items.
   */
  is_assigned_to_coach?: boolean;
  /**
   * MED-881: Computed server-side from GUARANTEED_LINE_ITEM_KEYS. True when
   * this line item is contractually guaranteed (paid regardless of
   * attendance/completion). The coach portal reads this flag to compute the
   * "Monthly Guaranteed" total and label items as guaranteed vs conditional —
   * replacing the implicit coupling to payment_track === 'flat_rate_monthly'.
   * MED-1383: in the coach portal payload it is true only once the CM confirmed
   * the coach's compensation.
   */
  is_guaranteed?: boolean;
  /**
   * MED-1383: coach portal only. A guaranteed-kind line whose compensation the CM
   * has not confirmed yet. The amount is an estimate and is labelled Estimated.
   */
  is_estimate?: boolean;
}

/** MED-677: Coach contract lifecycle used to gate line item labeling/filtering */
export type CohortCoachContractStatus = 'pending' | 'announced' | 'signed';

/** Estimation basis for compensation calculations */
export type EstimationBasis = 'pre_assignment' | 'actual_assignment';

/** Per-coach compensation result with line items and coach info */
export interface CoachCompensationResult {
  id: string;
  cohort_id: string;
  coach_user_id: string;
  status: CompensationCalcStatus;
  pending_reason: string | null;
  standard_total_cents: number | null;
  add_ons_total_cents: number | null;
  estimated_total_cents: number | null;
  flat_rate_monthly_cents: number | null;
  program_months: number | null;
  group_count: number;
  member_count: number;
  has_overridden_rates: boolean;
  estimation_basis: EstimationBasis;
  /** MED-910: per-coach excluded component keys (AC8–AC15). */
  excluded_components: string[];
  confirmed_at: string | null;
  confirmed_by: string | null;
  confirmed_by_name?: string;
  coach_name?: string;
  coach_email?: string;
  line_items: CompensationLineItem[];
  calculated_at: string;
  /** Coach pipeline stage (MED-663) */
  pool_status?: string;
  availability_status?: string;
  /** Per-coach compensation override for email (MED-663) */
  compensation_override_cents?: number | null;
  /**
   * MED-677: Coach contract lifecycle. Used by the CM view to render a
   * Confirmed/Potential badge on the coach list, and by the breakdown
   * drawer to decide whether to label line items as "Potential" (pre) or
   * render "N/A" for unassigned items (post).
   */
  contract_status?: CohortCoachContractStatus | null;
  /** MED-677: Announcement timestamp from cohort_coaches.announced_at */
  announced_at?: string | null;
  /** MED-677: Coach's confirmed modalities on the cohort */
  confirmed_modalities?: string[] | null;
}

/** GET /api/cohorts/:id/compensation/calculation response */
export interface CompensationCalculationResponse {
  status: string;
  cohort_id: string;
  total_estimated_cents: number;
  total_flat_rate_monthly_cents: number;
  total_attendance_dependent_cents: number;
  /** Totals for assigned coaches only (actual assignments) */
  assigned_total_estimated_cents: number;
  /** Totals for pre-assignment coaches only (estimates) */
  potential_total_estimated_cents: number;
  coach_count: number;
  program_months: number | null;
  system_calculated_count: number;
  confirmed_count: number;
  pending_count: number;
  coaches: CoachCompensationResult[];
  pending_coaches: CoachCompensationResult[];
  calculated_at: string | null;
}

// ============================================================
// Coach Portal Compensation (E13-4 / MED-508)
// ============================================================

/** Which compensation track a Bill.com payment belongs to (MDF-180) */
export type CoachPaymentTrack = 'flat_rate_monthly' | 'attendance_dependent_net_10';

/**
 * One Bill.com payment as the coach portal sees it (E13-7 AC13, MDF-180).
 * `decline_reason` is derived server-side from the status. The raw Bill.com
 * error_message is never sent to a coach (AC14), so there is no field here
 * that could leak it.
 */
export interface CoachPaymentRecord {
  process_date: string;
  amount_cents: number;
  status: string;
  memo: string | null;
  payment_track: CoachPaymentTrack;
  decline_reason: string | null;
  /** MED-1383: the date the payment is scheduled in Bill.com, as YYYY-MM-DD. */
  billcom_scheduled_date?: string | null;
  /**
   * MED-1383: the coach-facing row title, for example "Group coaching: Group 1" or
   * "Coach Immersion". The memo is for bookkeeping and is not shown to a coach.
   * Optional because a rolling deploy can serve an older API pod.
   */
  title?: string;
}

/** Per-cohort compensation data shown in coach portal */
export interface CoachCompensationCohort extends CoachCompensationResult {
  cohort_name: string;
  cohort_status: string;
  cohort_start_date: string | null;
  cohort_end_date: string | null;
  partner_name: string | null;
  /** Sum of line items on attendance_dependent_net_10 track */
  attendance_dependent_total_cents: number;
  /** Display status: real aggregate payment status from E13-7 */
  display_status: CompensationCalcStatus | 'fully_paid' | 'partially_paid' | 'payments_scheduled';
  /** Aggregate payment status (E13-7) */
  payment_status?: 'payments_scheduled' | 'partially_paid' | 'fully_paid' | null;
  /** Real flat-rate monthly payment data from Bill.com (E13-7 AC13) */
  monthly_payments?: CoachPaymentRecord[];
  /** Real attendance-dependent payment data from Bill.com (E13-7 AC13) */
  event_payments?: CoachPaymentRecord[];
  /** Estimated attendance-dependent total (E13-7 AC15) */
  estimated_attendance_total_cents?: number;
  /** Actual paid attendance-dependent total (E13-7 AC15) */
  actual_attendance_paid_cents?: number;
  /** Pre-computed payment schedule when no payments exist yet (E13-7 AC16) */
  payment_schedule?: Array<{ month: number; amount_cents: number; status: string }> | null;
}

/** GET /api/coach/compensation response */
export interface CoachCompensationOverview {
  cohorts: CoachCompensationCohort[];
}

/** Compensation audit log entry */
export interface CompensationAuditEntry {
  id: string;
  result_id: string;
  action: string;
  performed_by_name: string;
  details: Record<string, unknown>;
  created_at: string;
}

export interface Cohort {
  id: string;
  program_id: string;
  name: string;
  short_name: string | null;
  code: string | null;
  description: string | null;
  cohort_type: CohortType;
  status: CohortStatus;
  start_date: string | null;
  end_date: string | null;
  preferred_days: PreferredDay[];
  preferred_times: PreferredTime[];
  participant_capacity: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Key dates for task due date calculations
  coach_immersion_date: string | null;
  participant_orientation_date: string | null;
  coach_huddle_dates: (string | null)[];
  cohort_wrap_up_date: string | null;
  // General configuration
  default_meeting_link: string | null;
  default_meeting_platform: MeetingPlatform | null;
  // MED-1411: Zoom meeting ID and passcode in group session and workshop invites
  show_zoom_dial_in?: boolean;
  // Time slot display configuration
  time_slot_display_timezones: string[];
  // Email disable settings
  disabled_email_recipients: string[];
  // MED-438: Granular email settings
  email_settings: CohortEmailSettings;
  // MED-505: Audience type for compensation tier
  audience_type: AudienceType | null;
  // MED-645: Announcement email merge tag fields
  participant_description: string | null;
  matching_form_link: string | null;
  matching_form_deadline: string | null;
  info_session_date: string | null;
  // MED-778: Audience feature toggles
  participant_features_enabled: boolean;
  coach_features_enabled: boolean;
  // MED-1307 (MDF-184): raw column; has effect only when participant_features_enabled is true.
  participant_app_access_enabled?: boolean;
  // MED-1350: intro and midpoint check-ins
  intro_checkin_enabled: boolean;
  intro_checkin_target_week: number | null;
  midpoint_checkin_enabled: boolean;
  midpoint_checkin_target_week: number | null;
  // MED-1069 (E1.10c): commitment tracking relocated to the group_coaching
  // modality (Modality.commitment_tracking_enabled). The former cohort-level
  // flag (E1.10b) and DB column were removed in Flyway v230.
}

// MED-438: Granular email settings types
export type ParticipantEmailType =
  | 'session_reminder'
  | 'post_session_survey'
  | 'midpoint_survey'
  | 'endpoint_survey'
  | 'survey_nudge'
  | 'announcement'
  | 'intake'
  | 'pre_session_reflection';

export type CoachEmailType =
  | 'session_reminder'
  | 'coach_post_session_survey'
  | 'coach_availability_request'
  | 'coach_activated'
  | 'attendance_missing';

// MED-896: partner_survey_approval removed (dormant). Partners persona has zero
// individual email toggles; the type is intentionally empty until/if the
// partner survey approval workflow is built.
export type PartnerEmailType = never;

export interface PersonaEmailSettings<T extends string = string> {
  enabled: boolean;
  emails: Record<T, boolean>;
}

export interface CohortEmailSettings {
  master_enabled: boolean;
  personas: {
    participants: PersonaEmailSettings<ParticipantEmailType>;
    coaches: PersonaEmailSettings<CoachEmailType>;
    partners: PersonaEmailSettings<PartnerEmailType>;
  };
}

export type EmailPersona = 'participants' | 'coaches' | 'partners';

// Session types
// MED-1007 (E4.0g): 'missed' is the terminal no-show status for a peer-led Live
// Session Delivery session. Set server-side by E4.0f; a standard MED01 session
// never receives it. The FE renders it as a distinct "Missed" badge.
export type SessionStatus = 'scheduled' | 'completed' | 'cancelled' | 'missed';

// Display status for E8-13 (computed from scheduled_end and attendance_marked).
// MED-1007: 'missed' short-circuits the awaiting_attendance computation.
export type SessionDisplayStatus =
  | 'scheduled'
  | 'awaiting_attendance'
  | 'completed'
  | 'missed';

// RSVP status for calendar invites (MED-173)
export type RsvpStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

export interface Session {
  id: string;
  cohort_id: string;
  group_id: string | null;
  group_name?: string;
  session_number: number;
  title: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone?: string;
  status: SessionStatus;
  session_type: SessionType | null;
  modality_id: string | null;
  workshop_definition_id: string | null;
  zoom_link: string | null;
  // MED-780: Free-text physical location surfaced on the calendar invite.
  // When set, takes precedence over zoom_link as the calendar event location.
  location?: string | null;
  time_slot_identifier?: string;
  // Finalization tracking (MED-173)
  finalized_at?: string | null;
  finalized_by?: string | null;
  invites_sent_at?: string | null;
  invites_sent_by?: string | null;
  // Topic assignment (MED-187)
  topic_id?: string | null;
  topic_name?: string;
  // Attendance tracking (E8-13)
  attendance_marked?: boolean;
  attendance_marked_at?: string | null;
  attendance_marked_by?: string | null;
  // Computed display status (E8-13)
  display_status?: SessionDisplayStatus;
  // Coach feedback tracking (MED-89)
  feedback_submitted?: boolean;
  // E22-5d: 1:1 session type (topic for 1:1 coaching)
  one_on_one_type?: OneOnOneType | null;
  // MED-1350: the row's own length in minutes (sessions.duration_minutes).
  // Set on 1:1 slots from the cohort's session config. Null when not set.
  duration_minutes?: number | null;
  // MED-1350: the session's own coach, and how a 1:1 was booked. A Calendly
  // booking moves only through Calendly, so the date edit treats it apart.
  coach_id?: string | null;
  scheduling_method?: 'manual' | 'calendly' | 'calendly_fallback' | null;
  calendly_event_uri?: string | null;
  // MED-812 / E4.0a: Designated peer leader for this session (peer_led
  // modality only). NULL until designated. Display fields denormalized
  // server-side so the Sessions list/card can render without a second fetch.
  session_leader_id?: string | null;
  session_leader_first_name?: string | null;
  session_leader_last_name?: string | null;
  // MED-1162 — preferred name for the leader picker's full-name + "(preferred)"
  // parenthetical (AC5).
  session_leader_preferred_name?: string | null;
  session_leader_avatar_url?: string | null;
  // True iff the session's modality is group_coaching + peer_led. Server-
  // computed so the FE can gate the inline Leader cell without re-fetching
  // the modality.
  is_peer_led?: boolean;
  // MED-866 / E1.12: per-session AI Listening state (single source of truth,
  // sessions.ai_listening_enabled). Toggleable pre-lock by CM/coach/peer leader.
  ai_listening_enabled?: boolean;
  // MED-866 / E1.12: whether the cohort's group_coaching modality has AI
  // features enabled. Server-denormalized via the same LATERAL modality join
  // as is_peer_led so the Edit Session window can gate the AI Listening field
  // without a second modality fetch.
  ai_features_enabled?: boolean;
  // MED-1075 / E1.3k: server-derived content-authoring status for the
  // Sessions-tab badge. Null/undefined for non-group-coaching sessions (badge
  // is group-coaching only — AC9) → render no badge.
  content_status?: SessionContentStatus | null;
}

// MED-1075 / E1.3k: Sessions-tab content-status badge state. Surfaces states
// defined elsewhere (agenda completeness e1.3d, over-length e1.3j) — introduces
// no new rules. MED-1182 (E1.3f-R1) — pre-session work is optional and no
// longer part of completeness, so `prework_complete` was removed; a session is
// `complete` on its agenda alone. `state` is the primary badge; `over_length`
// is the additive "Review" chip. The booleans + minute figures let the FE
// compose per-state tooltip copy without recomputing.
export type SessionContentState = 'not_started' | 'in_progress' | 'complete';

export interface SessionContentStatus {
  state: SessionContentState;
  agenda_complete: boolean;
  over_length: boolean;
  agenda_total_minutes: number;
  session_duration_minutes: number | null;
}

// Session detail stats for modal header (MED-89)
export interface SessionDetailStats {
  total_sessions_in_group: number;
  participant_count: number;
  attendance_percentage: number | null;
  // MED-1266: marking coverage for the canonical attendance rate.
  attendance_marked: number;
  attendance_population: number;
  feedback_rating: number | null;
  avg_engagement: number | null;
}

// ============================================================================
// Topic Types (MED-187)
// ============================================================================

/**
 * Topic - predefined learning theme for sessions
 */
export interface Topic {
  id: string;
  name: string;
  description: string | null;
  display_order: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Cohort session topic assignment (zero state, before groups exist)
 */
export interface CohortSessionTopic {
  id: string;
  cohort_id: string;
  session_number: number;
  // MED-1190: nullable — a row can be resolved purely from the Content Editor's
  // per-session-number topic (session_number_config.topic), which stores the
  // topic name as free text and may not map to a topics row, so there is no
  // topic_id to return. Consumers fall back (e.g. `topic_id ?? selected.id`).
  topic_id: string | null;
  topic_name: string;
  // MED-832: optional participant-facing label override. NULL = participants see canonical topic_name.
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Session RSVP & Finalization Types (MED-173)
// ============================================================================

/**
 * RSVP summary counts for a session
 */
export interface SessionRsvpSummary {
  total: number;
  accepted: number;
  declined: number;
  pending: number;
  tentative: number;
}

/**
 * Group info for finalization status display
 */
export interface GroupFinalizationInfo {
  id: string;
  name: string;
  session_count: number;
  future_session_count: number;
  participant_count: number;
  // MED-838: Unique participants with no `accepted` or `declined` RSVP across
  // any of the group's finalized sessions. Drives the resend-invites callout.
  pending_unique_count: number;
  is_finalized: boolean;
  finalized_at: string | null;
  coach_id: string | null;
  coach_name: string | null;
  coach_zoom_link: string | null;
  coach_teams_link: string | null;
  coach_google_meet_link: string | null;
  // MED-780: Most recent non-empty `location` saved on this group's sessions
  // (prefers earliest future session). Used to pre-fill the FinalizeInvitesModal.
  saved_location: string | null;
}

/**
 * Finalization readiness status for a cohort
 */
export interface FinalizationStatus {
  ready: boolean;
  total_sessions: number;
  group_count: number;
  date_range: {
    start: string | null;
    end: string | null;
  };
  unfinalized_groups: GroupFinalizationInfo[];
  finalized_groups: GroupFinalizationInfo[];
}

/**
 * Request to finalize sessions and send invites
 *
 * MED-780: Both `video_link` and `location` are optional. The CM may finalize
 * with neither (e.g., for a session whose link will be set later) and the
 * resolution rule applies on the backend.
 */
export interface FinalizeSessionsRequest {
  group_ids?: string[];
  video_link?: string;
  location?: string;
}

/**
 * Response from finalize operation
 */
/**
 * MED-1300: a participant dropped from an invite send. Coaches are excluded by
 * the API (a Partner Coach without a Medley email is expected, MED-831) so
 * every entry here is a delivery failure worth acting on.
 */
export interface SkippedInviteParticipant {
  userId: string;
  name: string | null;
  reason: 'user_not_found' | 'missing_email';
}

/** MED-1300 (AC8): a captured Google Calendar failure, per session. */
export interface InviteCalendarFailure {
  sessionId: string;
  reason: string;
}

export interface FinalizeSessionsResponse {
  finalized_count: number;
  attendance_records_created: number;
  groups_finalized: string[];
  calendar_events_created?: number;
  emails_sent?: number;
  errors?: string[];
  // MED-1300: send-outcome fields — see SkippedInviteParticipant.
  skipped_participants?: SkippedInviteParticipant[];
  email_fallback_sessions?: number;
  sessions_processed?: number;
  calendar_failures?: InviteCalendarFailure[];
}

/**
 * Session with RSVP summary for list display
 */
export interface SessionWithRsvp extends Session {
  rsvp_summary?: SessionRsvpSummary;
}

/**
 * Session attendee with RSVP status
 */
export interface SessionAttendee {
  user_id: string;
  user_name: string;
  user_email: string;
  rsvp_status: RsvpStatus;
  rsvp_updated_at: string | null;
}

/**
 * Full RSVP details for a session
 */
export interface SessionRsvpDetails {
  session_id: string;
  is_finalized: boolean;
  summary: SessionRsvpSummary;
  attendees: SessionAttendee[];
}

// Primary contact for cohort notifications
export interface CohortPrimaryContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string | null;
}

export interface CohortWithDetails extends Cohort {
  program_name: string;
  partner_id: string;
  partner_name: string;
  partner_code: string | null;
  participant_count: number;
  coach_count?: number;
  task_count?: number;
  // Session config convenience fields (from primary modality)
  session_count?: number | null;
  completed_sessions?: number | null;
  session_frequency?: SessionFrequency | null;
  // Related data
  modalities?: ModalityWithWorkshops[];
  sessions?: Session[];
  date_constraints?: CohortDateConstraint[];
  team_assignments?: CohortTeamAssignment[];
  job_title_assignments?: CohortJobTitleAssignment[];
  primary_contacts?: CohortPrimaryContact[];
  // MED-114: Health status from attendance-based aggregate
  health_status?: OverallHealthStatus;
}

export interface CohortDateConstraint {
  id: string;
  cohort_id: string;
  start_date: string;
  end_date: string;
  label: string | null;
}

export interface CohortTeamAssignment {
  id: string;
  cohort_id: string;
  role: CohortTeamRole;
  user_id: string | null;
  user_name?: string;
  user_email?: string;
}

export interface SessionDateInput {
  session_number: number;
  scheduled_date: string;
}

export interface DateConstraintInput {
  start_date: string;
  end_date: string;
  label?: string;
}

export interface TeamAssignmentInput {
  role: CohortTeamRole;
  user_id: string | null;
}

export interface CreateCohortRequest {
  partner_id: string;
  program_id?: string;
  name: string;
  short_name?: string;
  code?: string;
  description?: string;
  cohort_type?: CohortType;
  start_date?: string;
  end_date?: string;
  preferred_days?: PreferredDay[];
  preferred_times?: PreferredTime[];
  participant_capacity?: number;
  modalities: CreateModalityInput[];
  session_dates?: SessionDateInput[];
  date_constraints?: DateConstraintInput[];
  team_assignments?: TeamAssignmentInput[];
  job_title_assignments?: { job_title_id: string; user_id: string | null }[];
  primary_contact_ids?: string[];
  // Key dates for cohort milestones
  coach_immersion_date?: string | null;
  participant_orientation_date?: string | null;
  coach_huddle_dates?: (string | null)[];
  cohort_wrap_up_date?: string | null;
  // General configuration
  default_meeting_link?: string;
  default_meeting_platform?: MeetingPlatform;
  // Email disable settings
  disabled_email_recipients?: string[];
  // MED-1350: intro and midpoint check-ins
  intro_checkin_enabled?: boolean;
  intro_checkin_target_week?: number | null;
  midpoint_checkin_enabled?: boolean;
  midpoint_checkin_target_week?: number | null;
}

export interface UpdateCohortRequest {
  name?: string;
  short_name?: string;
  code?: string;
  description?: string;
  status?: CohortStatus;
  start_date?: string;
  end_date?: string;
  preferred_days?: PreferredDay[];
  preferred_times?: PreferredTime[];
  participant_capacity?: number;
  is_active?: boolean;
  // Key dates for task due date calculations
  coach_immersion_date?: string | null;
  participant_orientation_date?: string | null;
  coach_huddle_dates?: (string | null)[];
  cohort_wrap_up_date?: string | null;
  // General configuration
  // MED-1360: null clears the field.
  default_meeting_link?: string | null;
  default_meeting_platform?: MeetingPlatform | null;
  show_zoom_dial_in?: boolean;
  // Announcement email fields (v150). null clears the field.
  participant_description?: string | null;
  matching_form_link?: string | null;
  matching_form_deadline?: string | null;
  info_session_date?: string | null;
  // MED-1360: false stops the PUT from regenerating task due dates.
  regenerate_due_dates?: boolean;
  // Email disable settings
  disabled_email_recipients?: string[];
  // MED-1350: intro and midpoint check-ins
  intro_checkin_enabled?: boolean;
  intro_checkin_target_week?: number | null;
  midpoint_checkin_enabled?: boolean;
  midpoint_checkin_target_week?: number | null;
  // MED-1069 (E1.10c): commitment_tracking_enabled removed from the cohort
  // update payload — now set per-modality via the modality settings PATCH.
}

// Coach types
export type CoachType = 'medley' | 'partner';
// MED-683: status is derived (Active when has approved modalities AND not manually inactive)
export type CoachStatus = 'active' | 'inactive';

/**
 * MED-1363: what can stop participant bookings on a coach's Calendly. The API
 * computes it and keeps it for 10 minutes.
 */
export interface CalendlyBookingHealth {
  plan_stage: 'trial' | 'free' | 'paid' | null;
  plan: string | null;
  /** Each mapped event type that has an enabled required question. */
  required_questions: Array<{
    event_type_name: string;
    durations: number[];
    question_names: string[];
  }>;
  /**
   * MED-1365: the coach address that a Google invite fallback uses. Null
   * when the coach has no email; missing from an older API.
   */
  invite_email?: string | null;
  /**
   * MED-1365 (QA round 2): how each session length books, from the active
   * flag of its mapped Calendly event type. 'off', 'unmapped' and 'refused'
   * book with a Google Calendar invite from the Medley calendar. Missing
   * from an older API or an older cached health.
   */
  durations?: CalendlyDurationHealth[];
  /**
   * MED-1365 QA round 7: on a free or trial plan whose 20 min length is not
   * on Calendly, the names of the coach's 20 min event types that are off.
   */
  off_20min_event_types?: string[];
}

/**
 * MED-1365 QA round 6 (comment 12672): a meeting platform that one of the
 * coach's current cohorts uses, while the coach profile has no link for it.
 */
export interface MissingMeetingLink {
  platform: 'zoom' | 'teams' | 'google_meet';
  cohorts: Array<{ id: string; name: string }>;
}

/** MED-1365 (QA round 2): how one session length books. */
export interface CalendlyDurationHealth {
  duration_minutes: 20 | 45 | 60;
  /**
   * calendly: the mapped event type is on. off: it is off in Calendly.
   * unmapped: none is mapped. refused: Calendly will not use the mapped
   * event type (it is gone, or Calendly refused its open times). unknown:
   * Calendly did not answer. wrong_length (QA round 6): the mapped event
   * type has another duration, so Medley does not use it.
   */
  state: 'calendly' | 'off' | 'unmapped' | 'refused' | 'wrong_length' | 'unknown';
  event_type_name: string | null;
  /** QA round 6: the mapped event type's duration. Missing from an older API. */
  event_type_duration?: number | null;
}

export interface Coach {
  id: string;
  user_id: string;
  coach_type: CoachType;
  partner_id: string | null;
  partner_name: string | null;
  status: CoachStatus;
  manually_inactive: boolean;
  expertise_areas: string[];
  hourly_rate_cents: number | null;
  notes: string | null;
  onboarded_at: string | null;
  created_at: string;
  updated_at: string;

  // Extended profile fields
  preferred_email: string | null;
  coaching_specializations: string;
  location: string;
  home_base_timezone: string;
  timezone_preference: string | null;
  birthday: string | null;
  race_ethnicity: string[];
  gender_identity: string | null;
  pronouns: string | null;
  additional_identity_notes: string | null;
  industry_experience: string[];
  facilitator_experience: boolean;
  certifications: string[];
  coaching_topics_of_interest: string | null;
  preferred_learning_experiences: string[];
  topics_wished_medley_addressed: string | null;
  modality_proficiencies: string[];
  modality_approvals?: string[];

  // Session settings (meeting links)
  zoom_link: string | null;
  // MED-1411: passcode of zoom_link, shown in invites when the cohort turns it on
  zoom_passcode?: string | null;
  teams_link: string | null;
  google_meet_link: string | null;

  // From users table
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  bio: string | null;
  avatar_url: string | null;

  // Magic link invitation tracking
  magic_link_invitation_sent_at: string | null;
  first_magic_link_login_at: string | null;

  // Calendly connection timestamp (null when not connected)
  calendly_connected_at: string | null;
  // MED-1363: what can stop participant bookings. Null when not connected
  // or when Calendly fails.
  calendly_booking_health?: CalendlyBookingHealth | null;
  /** MED-1365 QA round 6: meeting links the coach still needs. */
  missing_meeting_links?: MissingMeetingLink[];

  // Computed
  active_cohort_count: number;
  satisfaction_rating: number | null;
  satisfaction_survey_count: number;
}

export interface CoachActivityLog {
  id: string;
  coach_profile_id: string;
  action:
    | 'profile_created'
    | 'profile_updated'
    | 'status_changed'
    | 'avatar_updated'
    | 'invitation_sent'
    | 'invitation_resent'
    // MED-1355: a coach email changed. changes = { email: { old, new } } for
    // the Medley email, { preferred_email: { old, new } } for the personal email.
    | 'email_changed';
  description: string;
  changed_by: string | null;
  changed_by_name: string | null;
  changes: Record<string, { old: unknown; new: unknown }> | null;
  created_at: string;
}

export interface CoachCohortAssignment {
  cohort_id: string;
  cohort_name: string;
  cohort_status: string;
  partner_name: string | null;
  start_date: string | null;
  end_date: string | null;
  assigned_at: string;
  is_active: boolean;
}

export interface CreateCoachRequest {
  // Required
  first_name: string;
  last_name: string;
  location: string;
  home_base_timezone: string;

  // MED-831: optional — Partner Coaches may not have a Medley email or specializations on file.
  email?: string;
  coaching_specializations?: string;

  // Coach type (defaults to 'medley')
  coach_type?: CoachType;
  partner_id?: string;

  // Optional basic
  phone?: string;
  bio?: string;
  expertise_areas?: string[];
  notes?: string;
  preferred_email?: string;
  timezone_preference?: string;

  // Optional identity
  birthday?: string;
  race_ethnicity?: string[];
  gender_identity?: string;
  pronouns?: string;
  additional_identity_notes?: string;

  // Optional professional
  industry_experience?: string[];
  facilitator_experience?: boolean;
  certifications?: string[];

  // Optional learning
  coaching_topics_of_interest?: string;
  preferred_learning_experiences?: string[];
  topics_wished_medley_addressed?: string;
}

export interface UpdateCoachRequest {
  // Basic info
  first_name?: string;
  last_name?: string;
  /**
   * MED-1355: the Medley email (users.email), at a Medley domain. Empty or
   * null clears it, which the API refuses while the coach leads a group or
   * coaches a 1:1.
   * Leave it out to keep the current value.
   */
  email?: string | null;
  phone?: string;
  bio?: string;
  expertise_areas?: string[];
  /** MED-683: status is derived; use manually_inactive to set/clear the manual inactive flag. */
  manually_inactive?: boolean;
  notes?: string;
  hourly_rate_cents?: number;
  preferred_email?: string;
  coaching_specializations?: string;

  // Location & timezone
  location?: string;
  home_base_timezone?: string;
  timezone_preference?: string | null;

  // Identity
  birthday?: string | null;
  race_ethnicity?: string[];
  gender_identity?: string | null;
  pronouns?: string | null;
  additional_identity_notes?: string | null;

  // Professional
  industry_experience?: string[];
  facilitator_experience?: boolean;
  certifications?: string[];

  // Learning
  coaching_topics_of_interest?: string | null;
  preferred_learning_experiences?: string[];
  topics_wished_medley_addressed?: string | null;
  modality_proficiencies?: string[];
  modality_approvals?: string[];

  // Session settings
  zoom_link?: string | null;
  zoom_passcode?: string | null;
  teams_link?: string | null;
  google_meet_link?: string | null;
}

// Partner Contact types
export interface PartnerContact {
  id: string;
  partner_id: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string | null;
  internal_notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PartnerContactWithCohortCount extends PartnerContact {
  primary_cohort_count: number;
}

export interface PartnerCoachListItem {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  home_base_timezone: string | null;
  status: CoachStatus;
  assigned_group_count: number;
  avatar_url: string | null;
}

export interface CreatePartnerContactRequest {
  first_name: string;
  last_name: string;
  email: string;
  job_title?: string;
  internal_notes?: string;
}

export interface UpdatePartnerContactRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  job_title?: string;
  internal_notes?: string;
}

export interface PartnerContactCohort {
  id: string;
  name: string;
}

// Participant types
export type ParticipantStatus = 'pending' | 'active' | 'withdrawn';

export interface Participant {
  id: string;
  user_id: string;
  cohort_id: string;
  first_name: string;
  last_name: string;
  email: string;
  preferred_name: string | null;
  status: ParticipantStatus;
  group_id: string | null;
  group_name: string | null;
  partner_name: string | null;
  // Cohort-specific fields (stored on cohort_participant)
  personality_type: string | null;
  role: string | null;
  business_unit: string | null;
  location: string | null;
  manager: string | null;
  manager_email: string | null;
  level: string | null;
  dnm: string | null;
  dnm_groupings: string | null;
  // Custom fields (JSONB)
  custom_fields: Record<string, string> | null;
  // Intake form fields
  intake_form_url: string | null;
  intake_form_sent_at: string | null;
  intake_completed_at: string | null;
  intake_reminder_sent_at: string | null;
  intake_reminder_count: number;
  experience: string | null;
  primary_goal: string | null;
  intake_timezone: string | null;
  availability: string | null;
  direct_reports: string | null;
  development_goal: string | null;
  available_time_slots: string[] | null;
  // Computed
  attendance_percentage: number | null;
  sessions_attended: number;
  // MED-1266: total_sessions is the recorded-outcome count (the rate's denominator);
  // attendance_population is that plus still-pending outcomes (coverage denominator).
  total_sessions: number;
  attendance_population: number;
  enrolled_at: string;
  withdrawn_at: string | null;
  created_at: string;
  updated_at: string;
  // Survey completion
  midpoint_completed: boolean;
  endpoint_completed: boolean;
  // Concern flag fields
  has_open_flag: boolean;
  flag_id: string | null;
  flag_session_number: number | null;
  flag_reason: string | null;
  // Most recent email sent to participant
  latest_email_type: string | null;
  latest_email_sent_at: string | null;
}

// Cohort history entry for Activity tab (includes cohort details and attendance)
export interface CohortHistoryEntry {
  cohort_id: string;
  cohort_code: string | null;
  cohort_name: string;
  cohort_status: string; // 'live', 'completed', 'in_prep', etc.
  start_date: string | null;
  end_date: string | null;
  enrolled_at: string;
  sessions_attended: number;
  total_sessions: number;
  attendance_percentage: number | null;
}

export interface EmailHistoryEntry {
  id: string;
  email_type: string;
  subject: string;
  to_email: string;
  status: string;
  sent_at: string | null;
  created_at: string;
}

export interface AbsenceEmailHistoryEntry {
  id: string;
  session_number: number;
  session_date: string;
  session_timezone: string;
  template_name: string | null;
  rendered_subject: string | null;
  rendered_body: string | null;
  sent_at: string | null;
  sent_by_name: string | null;
  participant_name: string | null;
  participant_email: string | null;
  absence_type: string;
  cumulative_absence_count: number;
}

export interface ParticipantCohortAttendance {
  sessions_attended: number;
  sessions_partial: number;
  sessions_absent: number;
  // MED-1266: total_sessions is the recorded-outcome count (the rate's denominator).
  total_sessions: number;
  attendance_percentage: number | null;
  // MED-1266: marking coverage — attendance_marked of attendance_population marked.
  attendance_marked: number;
  attendance_population: number;
  engagement_ratings: Array<{
    session_number: number | null;
    session_title: string;
    rating: number;
    coach_name: string;
    session_date: string | null;
  }>;
  average_engagement_rating: number | null;
}

export interface ParticipantListResponse {
  data: Participant[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    total: number;
    unassigned: number;
    avgAttendance: number | null;
  };
}

// CSV Import types
export interface CSVColumnMapping {
  csvColumn: string;
  targetField: string | null;
  isCustomField: boolean;
  customFieldName: string | null;
}

export interface CSVImportPreviewRow {
  rowIndex: number;
  status: 'valid' | 'warning' | 'error' | 'duplicate' | 'update';
  errors: string[];
  warnings: string[];
  data: {
    first_name: string;
    last_name: string;
    email: string;
    preferred_name?: string;
    personality_type?: string;
    role?: string;
    business_unit?: string;
    location?: string;
    manager?: string;
    level?: string;
    dnm?: string;
    dnm_groupings?: string;
    [key: string]: string | undefined;
  };
  existingParticipantId?: string;
  existingUserId?: string;
}

export interface CSVImportPreview {
  totalRows: number;
  validRows: number;
  warningRows: number;
  errorRows: number;
  duplicateRows: number;
  rowsToUpdate: number;
  newUsersToCreate: number;
  existingUsersToEnroll: number;
  customFields: string[];
  rows: CSVImportPreviewRow[];
}

export interface CSVImportResult {
  success: boolean;
  imported: number;
  updated: number;
  skipped: number;
  usersCreated: number;
  customFieldsCreated: string[];
  errors: Array<{ row: number; error: string }>;
}

// ============================================================================
// Job Title Types
// ============================================================================

/**
 * Job title for task assignment
 */
export interface JobTitle {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  category: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Cohort job title assignment with user details
 */
export interface CohortJobTitleAssignment {
  id: string;
  cohort_id: string;
  job_title_id: string;
  job_title_name: string;
  job_title_description: string | null;
  job_title_category: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
}

/**
 * Input for updating job title assignments
 */
export interface JobTitleAssignmentInput {
  job_title_id: string;
  user_id: string | null;
}

// ============================================================================
// Task Types
// ============================================================================

/**
 * Task status
 */
export type TaskStatus = 'not_started' | 'in_progress' | 'complete' | 'blocked' | 'cancelled';

/**
 * Template type for task templates
 */
export type TemplateType = 'base' | 'group_coaching' | 'one_on_one' | 'workshop';

/**
 * Task priority
 */
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

/**
 * Task phase
 */
export type TaskPhase = 'cohort_setup' | 'cohort_prep' | 'live_delivery' | 'wrap_renewal';

/**
 * Task action type for activity logging
 */
export type TaskActionType =
  | 'created'
  | 'updated'
  | 'status_changed'
  | 'assignee_changed'
  | 'due_date_changed'
  | 'comment_added'
  | 'deleted';

/**
 * Task from API
 */
export interface Task {
  id: string;
  task_group_id: string | null;
  cohort_id: string;
  parent_task_id: string | null;
  session_id: string | null;
  job_title_id: string | null;
  title: string;
  description: string | null;
  assignee_id: string | null;
  created_by: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  phase: TaskPhase | null;
  sub_group: string | null;
  due_date: string | null;
  due_time: string | null;
  anchor_type: TaskAnchorType;
  anchor_session_number: number | null;
  days_offset: number;
  completed_at: string | null;
  completed_by: string | null;
  display_order: number;
  blocked_by_task_id: string | null;
  template_task_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Task with joined details for display
 */
export interface TaskWithDetails extends Task {
  assignee_name: string | null;
  assignee_email: string | null;
  job_title_name: string | null;
  session_title: string | null;
  sub_group_cohort_status: CohortStatus | null; // MED-160: Cohort status mapped to this sub-group
  sub_group_sort_order: number | null;
  subtask_count: number;
  subtasks_completed: number;
  comment_count: number;
  has_overdue_subtasks: boolean;
}

/**
 * Full session details for task's Related Session card
 */
export interface SessionDetailsForTask {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  status: string;
  zoom_link: string | null;
  group_id: string | null;
  group_name: string | null;
  coach_id: string | null;
  coach_name: string | null;
  expected_participants: number;
  finalized_at: string | null;
  rsvp_summary?: SessionRsvpSummary;
}

/**
 * Task with subtasks and comments for detail view
 */
export interface TaskDetail extends TaskWithDetails {
  subtasks: TaskWithDetails[];
  comments: TaskComment[];
  session_details?: SessionDetailsForTask | null;
}

/**
 * Task comment
 */
export interface TaskComment {
  id: string;
  task_id: string;
  author_id: string;
  content: string;
  author_name: string;
  author_email: string;
  author_avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Task activity log entry
 */
export interface TaskActivityLog {
  id: string;
  task_id: string;
  user_id: string | null;
  action_type: TaskActionType;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  user_name: string | null;
  user_email: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/**
 * Task statistics for a cohort
 */
export interface TaskStats {
  total: number;
  not_started: number;
  in_progress: number;
  complete: number;
  blocked: number;
  overdue: number;
}

/**
 * Create task request
 */
export interface CreateTaskRequest {
  title: string;
  description?: string;
  assignee_id?: string;
  task_group_id?: string;
  parent_task_id?: string;
  session_id?: string;
  job_title_id?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  phase?: TaskPhase;
  sub_group?: string;
  due_date?: string;
  due_time?: string;
}

/**
 * Update task request
 */
export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  assignee_id?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  phase?: TaskPhase;
  due_date?: string | null;
  due_time?: string | null;
  anchor_type?: TaskAnchorType;
  anchor_session_number?: number | null;
  days_offset?: number;
  display_order?: number;
}

/**
 * Task filter options
 */
export interface TaskFilters {
  status?: TaskStatus[];
  phase?: TaskPhase[];
  assignee_id?: string;
  session_id?: string;
  include_subtasks?: boolean;
}

/**
 * Bulk task operation
 */
export interface BulkTaskRequest {
  task_ids: string[];
  action: 'update_status' | 'update_assignee' | 'update_priority' | 'delete';
  value?: string;
}

/**
 * Milestone
 */
export interface Milestone {
  id: string;
  cohort_id: string;
  name: string;
  description: string | null;
  display_order: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'blocked';
  target_start_date: string | null;
  target_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  progress_percent: number;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Task Template Types
// ============================================================================

/**
 * Task anchor type for due date calculation
 */
export type TaskAnchorType =
  | 'cohort_start'
  | 'cohort_end'
  | 'orientation'
  | 'coach_kickoff'
  | 'session'
  | 'final_session'
  | 'midpoint'
  | 'endpoint';

/**
 * Task template from database
 */
export interface TaskTemplate {
  id: string;
  cohort_type: string;
  template_type: TemplateType;
  phase: TaskPhase | null;
  phase_id: string | null;
  sub_group: string | null;
  sub_group_id: string | null;
  milestone_name: string;
  task_group_name: string;
  task_title: string;
  task_description: string | null;
  job_title_id: string | null;
  parent_template_id: string | null;
  anchor_type: TaskAnchorType;
  anchor_session_number: number | null;
  days_offset: number;
  default_priority: TaskPriority;
  milestone_order: number;
  task_group_order: number;
  task_order: number;
  is_required: boolean;
  is_session_specific: boolean;
  is_active: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Task template with joined details for display
 */
export interface TaskTemplateWithDetails extends TaskTemplate {
  job_title_name: string | null;
  parent_task_title: string | null;
  phase_name: string | null;
  phase_slug: string | null;
  sub_group_name: string | null;
  subtask_count: number;
}

/**
 * Create task template request
 */
export interface CreateTaskTemplateRequest {
  cohort_type?: string;
  template_type?: TemplateType;
  phase?: TaskPhase;
  phase_id?: string;
  sub_group?: string;
  sub_group_id?: string | null;
  milestone_name?: string;
  task_group_name?: string;
  task_title: string;
  task_description?: string;
  job_title_id?: string | null;
  parent_template_id?: string;
  anchor_type?: TaskAnchorType;
  anchor_session_number?: number;
  days_offset?: number;
  default_priority?: TaskPriority;
  milestone_order?: number;
  task_group_order?: number;
  task_order?: number;
  is_required?: boolean;
  is_session_specific?: boolean;
}

/**
 * Update task template request
 */
export interface UpdateTaskTemplateRequest {
  cohort_type?: string;
  template_type?: TemplateType;
  phase?: TaskPhase | null;
  phase_id?: string | null;
  sub_group?: string | null;
  sub_group_id?: string | null;
  milestone_name?: string;
  task_group_name?: string;
  task_title?: string;
  task_description?: string | null;
  job_title_id?: string | null;
  parent_template_id?: string | null;
  anchor_type?: TaskAnchorType;
  anchor_session_number?: number | null;
  days_offset?: number;
  default_priority?: TaskPriority;
  milestone_order?: number;
  task_group_order?: number;
  task_order?: number;
  is_required?: boolean;
  is_active?: boolean;
}

// ============================================================================
// Task Phase Types (Admin-editable phases)
// ============================================================================

/**
 * Task phase record from database
 */
export interface TaskPhaseRecord {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Task phase with template count for admin display
 */
export interface TaskPhaseWithCount extends TaskPhaseRecord {
  template_count: number;
}

/**
 * Request to create a task phase
 */
export interface CreateTaskPhaseRequest {
  name: string;
  slug?: string;
  description?: string;
  sort_order?: number;
}

/**
 * Request to update a task phase
 */
export interface UpdateTaskPhaseRequest {
  name?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

// ============================================================================
// Task Sub-Group Types
// ============================================================================

/**
 * Task sub-group record from database
 */
export interface TaskSubGroup {
  id: string;
  phase_id: string;
  name: string;
  description: string | null;
  cohort_status: CohortStatus | null; // MED-160: Maps sub-group to cohort status
  sort_order: number;
  is_active: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Task sub-group with phase name and template count
 */
export interface TaskSubGroupWithDetails extends TaskSubGroup {
  phase_name: string;
  phase_slug: string;
  template_count: number;
}

/**
 * Request to create a task sub-group
 */
export interface CreateTaskSubGroupRequest {
  phase_id: string;
  name: string;
  description?: string;
  cohort_status?: CohortStatus | null;
  sort_order?: number;
}

/**
 * Request to update a task sub-group
 */
export interface UpdateTaskSubGroupRequest {
  name?: string;
  description?: string | null;
  cohort_status?: CohortStatus | null;
  sort_order?: number;
  is_active?: boolean;
}

// ============================================================================
// Grouped Template Types (for hierarchical display)
// ============================================================================

/**
 * Sub-group with its templates for hierarchical display
 */
export interface SubGroupWithTemplates extends TaskSubGroup {
  phase_name: string;
  templates: TaskTemplateWithDetails[];
}

/**
 * Phase with its sub-groups and templates for hierarchical display
 */
export interface PhaseWithSubGroups extends TaskPhaseRecord {
  template_count: number;
  sub_groups: SubGroupWithTemplates[];
}

// ============================================================================
// Resource Types
// ============================================================================

/**
 * Resource type - file or link
 */
export type ResourceType = 'file' | 'link';

/**
 * Access level for filtering resources
 */
export type ResourceAccessLevel = 'coaches' | 'participants' | 'partners';

/**
 * Resource category
 */
export interface ResourceCategory {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

/**
 * Resource base type
 */
export interface Resource {
  id: string;
  cohort_id: string | null;
  uploaded_by: string;
  category_id: string | null;
  title: string;
  description: string | null;
  resource_type: ResourceType;
  file_path: string | null;
  file_size: number | null;
  mime_type: string | null;
  original_filename: string | null;
  url: string | null;
  is_evergreen: boolean;
  access_coaches: boolean;
  access_participants: boolean;
  access_partners: boolean;
  created_at: string;
}

/**
 * Session association details for display
 */
export interface ResourceSessionDetail {
  session_id: string;
  session_number: number | null;
  session_type: string | null;
  title: string | null;
  group_id: string | null;
  group_name: string | null;
  is_all_groups: boolean;
}

/**
 * Resource with joined details for display
 */
export interface ResourceWithDetails extends Resource {
  category_name: string | null;
  uploaded_by_name: string;
  download_url: string | null;
  sessions: ResourceSessionDetail[];
}

/**
 * Resource counts for a cohort
 */
export interface ResourceCounts {
  total: number;
  evergreen: number;
}

/**
 * Create file resource request
 */
export interface CreateFileResourceRequest {
  cohort_id?: string;
  session_ids?: string[];
  category_id?: string;
  title: string;
  description?: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  original_filename: string;
  is_evergreen?: boolean;
  access_coaches?: boolean;
  access_participants?: boolean;
  access_partners?: boolean;
}

/**
 * Create link resource request
 */
export interface CreateLinkResourceRequest {
  cohort_id?: string;
  session_ids?: string[];
  category_id?: string;
  title: string;
  description?: string;
  url: string;
  is_evergreen?: boolean;
  access_coaches?: boolean;
  access_participants?: boolean;
  access_partners?: boolean;
}

/**
 * Update resource request
 */
export interface UpdateResourceRequest {
  title?: string;
  description?: string;
  category_id?: string;
  session_ids?: string[];
  access_coaches?: boolean;
  access_participants?: boolean;
  access_partners?: boolean;
}

/**
 * Resource filter options
 */
export interface ResourceFilterOptions {
  session_ids?: string[];
  category_id?: string;
  search?: string;
  access_level?: ResourceAccessLevel;
}

/**
 * Presigned upload URL response
 */
export interface PresignedUploadResponse {
  upload_url: string;
  download_url: string;
  key: string;
  expires_in: number;
}

// ============================================================================
// Cohort Overview Types (MED-158)
// ============================================================================

/**
 * Health status for a metric
 */
export type HealthStatus = 'healthy' | 'warning' | 'critical' | 'no_data';

/**
 * Milestone status
 */
export type MilestoneStatus = 'not_started' | 'in_progress' | 'completed';

/**
 * Activity event type
 */
export type ActivityEventType =
  | 'participant_added'
  | 'task_completed'
  | 'group_created'
  | 'coach_assigned'
  | 'intake_completed'
  | 'dates_updated'
  | 'survey_scheduled'
  | 'survey_cancelled'
  | 'survey_sent'
  | 'status_changed'
  | 'configuration_updated'
  | 'absence_follow_up_sent'
  // MED-1300: durable invite-send record (review feedback — was falling
  // through to the generic gray "Activity" rendering).
  | 'session_invites_sent'
  // MED-1297: failed/partial calendar guest-list sync after a withdrawal,
  // re-activation, or group removal.
  | 'calendar_sync_failed'
  // MED-1301: pre-session reflection sends — written by the API since MED-936
  // but rejected by the DB enum until medley-db v247.
  | 'pre_session_reflection_prompt_sent'
  | 'pre_session_reflection_reminder_sent';

/**
 * Session stats for overview
 */
export interface OverviewSessionStats {
  completed: number;
  total: number;
  progressPercent: number;
}

/**
 * Participant stats for overview
 */
export interface OverviewParticipantStats {
  total: number;
  active: number;
}

/**
 * Group stats for overview
 */
export interface OverviewGroupStats {
  count: number;
  avgParticipantsPerGroup: number;
  changeThisWeek: number | null;
}

/**
 * Task stats for overview
 */
export interface OverviewTaskStats {
  completed: number;
  total: number;
  progressPercent: number;
}

/**
 * Average attendance stat
 */
export interface OverviewAttendanceStats {
  percent: number | null;
  completedSessions: number;
  changeThisWeek: number | null;
  // MED-1266: marking coverage — recorded outcomes (marked) of the population the rate covers.
  // NOTE: the backend supplies these on avgAttendance, but no FE component currently renders
  // avgAttendance; the live overview attendance stat reads HealthMetric (health.attendance),
  // which the backend does NOT extend with coverage. See MED-1266 follow-up.
  marked: number;
  population: number;
}

/**
 * Average session rating stat
 */
export interface OverviewSessionRatingStats {
  value: number | null;
  responseCount: number;
  changeThisWeek: number | null;
}

/**
 * Milestone (phase) progress for overview
 */
export interface OverviewMilestoneProgress {
  phaseId: string;
  phaseName: string;
  phaseSlug: string;
  subGroups: string[];
  status: MilestoneStatus;
  progressPercent: number;
  tasksCompleted: number;
  tasksTotal: number;
  sortOrder: number;
}

/**
 * Activity event for the recent activity feed
 */
export interface OverviewActivityEvent {
  id: string;
  type: ActivityEventType;
  description: string;
  timestamp: string;
  actorName: string | null;
}

/**
 * Health metric value with status
 */
export interface HealthMetric {
  value: number | null;
  status: HealthStatus;
}

/**
 * Health signals section
 */
export interface OverviewHealthSignals {
  attendance: HealthMetric;
  taskCompletion: HealthMetric;
  satisfaction: HealthMetric;
}

/**
 * Complete overview stats response
 */
export interface CohortOverviewStats {
  sessions: OverviewSessionStats;
  participants: OverviewParticipantStats;
  groups: OverviewGroupStats;
  tasks: OverviewTaskStats;
  avgAttendance: OverviewAttendanceStats;
  avgSessionRating: OverviewSessionRatingStats;
  milestones: OverviewMilestoneProgress[];
  recentActivity: OverviewActivityEvent[];
  health: OverviewHealthSignals;
}

// ============================================================================
// Modality Progress Types (MED-570)
// ============================================================================

export interface ModalityProgressCard {
  modality_type: 'group_coaching' | 'one_on_one' | 'workshop';
  label: string;
  completed: number;
  total: number;
  percentage: number;
}

// ============================================================================
// Health Summary Types (MED-114)
// ============================================================================

/** Alert severity tier */
export type AlertTier = 'risk' | 'monitor' | 'on_track' | 'notification';

/** Overall cohort health status */
export type OverallHealthStatus = 'at_risk' | 'monitor' | 'on_track' | 'no_data';

/** Trend direction for metrics */
export type TrendDirection = 'up' | 'down' | 'stable';

/** Entity types that alerts can target */
export type AlertEntityType = 'cohort' | 'group' | 'participant' | 'session' | 'coach';

/** Trend data for a metric */
export interface MetricTrend {
  direction: TrendDirection;
  change: number;
  /** Latest session value for tooltip display */
  latestValue: number | null;
  /** Cohort average value for tooltip display */
  averageValue: number | null;
}

/** MED-421: Recent session card for health summary header */
export interface RecentSessionCard {
  sessionNumber: number;
  title: string;
  date: string | null;
  isFlagged: boolean;
}

/** Aggregated metrics for the health summary header */
export interface HealthSummaryMetrics {
  engagementPercent: number | null;
  /** MED-743: Raw 1-5 engagement average, rounded to 1 decimal */
  engagementAvg: number | null;
  attendancePercent: number | null;
  avgRating: number | null;
  /** MED-772: Survey-based satisfaction average (from form_submissions), distinct from avgRating (session feedback). */
  avgSatisfaction: number | null;
  /** MED-772: Top-two-box satisfaction percentage (ratings >= 4 out of all non-null ratings) */
  satisfactionTopTwoBoxPct: number | null;
  engagementTrend: MetricTrend;
  attendanceTrend: MetricTrend;
  ratingTrend: MetricTrend;
}

/** Improvement type for resolved alerts displayed in On Track */
export type ImprovementType = 'RECOVERY' | 'GROWTH' | 'SESSIONS' | 'RATING' | 'DAYS';

/** Individual health alert */
export interface HealthAlert {
  id: string;
  ruleKey: string;
  tier: AlertTier;
  entityType: AlertEntityType;
  entityId: string;
  entityName: string;
  title: string;
  description: string;
  statValue: string;
  statLabel: string;
  suggestedAction: string;
  isDismissed: boolean;
  /** Optional participant ID for group-entity alerts that need participant-level dismissal tracking */
  participantId?: string;
  resolutionType?: 'dismissed' | 'addressed';
  dismissedByName?: string;
  dismissedAt?: string;
  dismissedNotes?: string | null;
  isImprovement?: boolean;
  improvementType?: ImprovementType;
  historyId?: string;
}

/** Alert tier data with count, summary, and alerts */
export interface HealthTierData {
  count: number;
  summary: string;
  alerts: HealthAlert[];
}

/** MED-419: Individual stat card data */
export interface HealthStatCard {
  value: string;
  label: string;
  badgeLabel: string | null;
  badgeColor: 'red' | 'orange' | 'green' | 'gray' | null;
  issueSummary: string;
  issueColor: 'red' | 'orange' | 'green' | 'gray';
  trendValue: number | null;
  trendDirection: TrendDirection | null;
  trendTooltip: string | null;
  /** MED-772: Multiline tooltip for the card value (e.g. satisfaction explanation) */
  valueTooltip: string | null;
}

/** MED-419: Stat cards for cohort health drill-down */
export interface HealthStatCards {
  sessions: HealthStatCard;
  participants: HealthStatCard;
  attendance: HealthStatCard;
  satisfaction: HealthStatCard;
}

/** MED-420: Coach insight detail for modal */
export interface CoachInsightDetail {
  groupName: string;
  coachName: string;
  coachAvatarUrl: string | null;
  text: string;
}

/** MED-420: Session drill-down card */
export interface SessionDrillDownCard {
  sessionNumber: number;
  title: string;
  date: string | null;
  status: 'complete' | 'partial' | 'flagged' | 'scheduled';
  statusLabel: string;
  completedGroups: number;
  totalGroups: number;
  isFlagged: boolean;
  /** MED-661: Plain-text flag reasons for tooltip display */
  flagReasons: string[];
  metrics: {
    attendPercent: number | null;
    engageAvg: number | null;
    rsvpPercent: number | null;
    /**
     * MED-1205 (C2b-ii): leader/coach share of talk time for this session,
     * from B7's stored session_airtime (MED-894) — never recomputed. `null` =
     * "not available" (no diarized transcript for any group, B7 AC7); render
     * the not-available state, never 0%.
     */
    airtimeLeaderPct: number | null;
  };
  coachInsight: {
    text: string;
    coachName: string;
    groupName: string;
  } | null;
  allCoachInsights: CoachInsightDetail[];
}

/** MED-420: Session drill-down data for health summary */
export interface SessionDrillDown {
  flaggedCount: number;
  tabs: {
    completed: { count: number; sessions: SessionDrillDownCard[] };
    upcoming: { count: number; sessions: SessionDrillDownCard[] };
    flagged: { count: number; sessions: SessionDrillDownCard[] };
  };
}

/** Complete health summary API response */
export interface HealthSummaryResponse {
  overallStatus: OverallHealthStatus;
  statusMessage: string;
  metrics: HealthSummaryMetrics;
  tiers: {
    risk: HealthTierData;
    monitor: HealthTierData;
    onTrack: HealthTierData;
    notifications: HealthTierData;
  };
  statCards: HealthStatCards;
  sessionDrillDown: SessionDrillDown;
  /** MED-421: Last 3 completed sessions for header display */
  recentSessions: RecentSessionCard[];
}

/** Request body for dismissing an alert */
export interface DismissAlertRequest {
  ruleKey: string;
  entityId: string;
  notes?: string;
  triggerSnapshot?: Record<string, unknown>;
}

// ============================================================================
// Participant Flag Types (MED-446)
// ============================================================================

export type ParticipantFlagSource = 'coach' | 'system';
export type ParticipantFlagCategory = 'attendance' | 'participant_feedback' | 'engagement' | 'coach_flag';
export type ParticipantFlagStatus = 'active' | 'dismissed' | 'addressed' | 'auto_resolved';

export interface ParticipantFlagWithDetails {
  id: string;
  participant_id: string;
  cohort_id: string;
  source_type: ParticipantFlagSource;
  source_id: string;
  flag_category: ParticipantFlagCategory;
  flag_reason: string;
  session_id: string | null;
  status: ParticipantFlagStatus;
  resolution_method: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  resolution_reason: string | null;
  created_at: string;
  updated_at: string;
  participant_name: string;
  participant_email: string;
  source_name: string;
  session_title: string | null;
  session_number: number | null;
  session_date: string | null;
  resolved_by_name: string | null;
}

// ============================================================================
// Attendance Drill-Down Types (MED-425)
// ============================================================================

export type AttendanceParticipantStatus = 'action' | 'monitor' | 'healthy';

export interface SessionIndicator {
  sessionNumber: number;
  status: 'attended' | 'absent' | 'partial' | 'excused' | 'pending' | null;
  attendedCount?: number;
  totalCount?: number;
}

export interface AttendanceDrillDownParticipant {
  participantId: string;
  name: string;
  initials: string;
  groupId: string | null;
  groupName: string | null;
  attendancePercent: number | null;
  sessionIndicators: SessionIndicator[];
  status: AttendanceParticipantStatus;
  lastSgosEmailAt: string | null;
  isFlagged: boolean;
  /** MED-476: Whether participant has attendance-specific flags */
  hasAttendanceFlag: boolean;
  /** MED-493: Participant enrollment status */
  participantStatus: string;
  /** MED-493: When the participant was withdrawn */
  withdrawnAt: string | null;
}

export interface FlaggedParticipantInfo {
  name: string;
  flags: { category: string; reason: string }[];
}

export interface AttendanceDrillDownGroupDetail {
  participantsFlagged: number;
  flaggedNames: string[];
  flaggedParticipants: FlaggedParticipantInfo[];
  avgAttendance: number | null;
  latestSessionEngagement: number | null;
  latestSessionNumber: number | null;
  surveyFlagCount: number;
  surveyFlagNames: string[];
}

export interface AttendanceDrillDownGroup {
  groupId: string;
  groupName: string;
  groupInitials: string;
  participantCount: number;
  coachName: string | null;
  attendancePercent: number | null;
  engagementAvg: number | null;
  sessionIndicators: SessionIndicator[];
  status: AttendanceParticipantStatus;
  trendPercent: number | null;
  trendDirection: TrendDirection;
  detail: AttendanceDrillDownGroupDetail;
  coachNotes: string | null;
}

export interface AttendanceDrillDownSession {
  sessionNumber: number;
  title: string;
  date: string | null;
  attendancePercent: number | null;
  engagementAvg: number | null;
  totalParticipants: number;
  attendedCount: number;
  absentCount: number;
  isFlagged: boolean;
}

export interface AttendanceDrillDownResponse {
  header: {
    title: string;
    warningText: string | null;
    flaggedCount: number;
  };
  groups: Array<{ id: string; name: string }>;
  participants: AttendanceDrillDownParticipant[];
  sessions: AttendanceDrillDownSession[];
  groupRows: AttendanceDrillDownGroup[];
}

// ============================================================================
// MED-426: Satisfaction Drill-Down Types
// ============================================================================

export type SatisfactionSentiment = 'very_satisfied' | 'satisfied' | 'neither' | 'dissatisfied' | 'very_dissatisfied';
export type SurveyTypeLabel = 'session' | 'midpoint' | 'endpoint';
export type CoachEngagementLevel = 'very_high' | 'high' | 'average' | 'low' | 'very_low';

export interface DistributionBar {
  label: string;
  count: number;
}

export interface FlaggedSurveyCallout {
  participantNames: string[];
  sentiment: string;
  quoteSnippet: string | null;
}

export interface SatisfactionSurveyRow {
  participantId: string;
  participantName: string;
  initials: string;
  groupId: string | null;
  groupName: string | null;
  surveyType: SurveyTypeLabel;
  surveyLabel: string;
  satisfactionScore: number;
  sentiment: SatisfactionSentiment;
  responseText: string | null;
  completedAt: string;
  isFlagged: boolean;
}

export interface CoachEngagementRow {
  sessionId: string;
  sessionNumber: number;
  sessionTitle: string;
  sessionDate: string | null;
  groupId: string | null;
  coachId: string;
  coachName: string;
  coachAvatarUrl: string | null;
  engagementLevel: CoachEngagementLevel;
  avgRating: number;
  statusReportText: string | null;
}

export interface LatestCoachReport {
  sessionTitle: string;
  sessionDate: string | null;
  reportText: string;
  coachName: string;
  coachAvatarUrl: string | null;
  coachInitials: string;
}

export interface SatisfactionDrillDownResponse {
  header: {
    title: string;
    flaggedCount: number;
    calloutText: string | null;
  };
  groups: Array<{ id: string; name: string }>;
  overview: {
    participantSatisfaction: {
      distribution: DistributionBar[];
      flaggedCount: number;
      flaggedCallout: FlaggedSurveyCallout | null;
    };
    coachFeedback: {
      distribution: DistributionBar[];
      latestReport: LatestCoachReport | null;
    };
  };
  participantSurveys: {
    flaggedCount: number;
    rows: SatisfactionSurveyRow[];
  };
  coachSurveys: {
    sessions: Array<{ sessionNumber: number; title: string }>;
    rows: CoachEngagementRow[];
  };
  engagementRatings: Array<{ rating: number; groupId: string | null }>;
}

// ============================================================================
// Time Slot Types (MED-172)
// ============================================================================

/**
 * Day of week for time slot scheduling
 */
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';

/**
 * Time slot for cohort session scheduling
 */
export interface TimeSlot {
  id: string;
  cohort_id: string;
  identifier: string;
  slot_number: number;
  day_of_week: DayOfWeek;
  time: string;
  timezone: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

/**
 * Time slot with formatted display values
 */
export interface TimeSlotResponse extends TimeSlot {
  display_time: string;
  display_day: string;
  timezone_abbreviation: string;
}

/**
 * Create time slot request
 */
export interface CreateTimeSlotRequest {
  day_of_week: DayOfWeek;
  time: string;
  timezone: string;
}

/**
 * Update time slot request (identifier and slot_number are immutable)
 */
export interface UpdateTimeSlotRequest {
  day_of_week?: DayOfWeek;
  time?: string;
  timezone?: string;
}

/**
 * Time slot list response
 */
export interface TimeSlotListResponse {
  time_slots: TimeSlotResponse[];
  total: number;
}

// ============================================================================
// Cross-Cohort Task Types (MED-190)
// ============================================================================

/**
 * Task row for cross-cohort task view with joined cohort/partner details
 */
export interface CrossCohortTaskRow {
  id: string;
  title: string;
  cohort_id: string;
  cohort_name: string;
  cohort_code: string | null;
  phase_name: string | null;
  partner_name: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  assignee_email: string | null;
  subtask_count: number;
  subtasks_completed: number;
  has_overdue_subtasks: boolean;
  parent_task_id: string | null;
  parent_task_title: string | null;
}

/**
 * Assignee option for the task filter avatars
 */
export interface TaskAssignee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

/**
 * Filters for cross-cohort task queries
 */
export interface CrossCohortTaskFilters {
  search?: string;
  status?: TaskStatus[];
  phase?: string[];
  cohortIds?: string[];
  assigneeIds?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  isOverdue?: boolean;
  includeOverdue?: boolean;
  flatten?: boolean;
  sortBy?: 'due_date' | 'partner' | 'cohort' | 'status';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export type TaskGroupByField = 'cohort' | 'phase' | 'status' | 'assignee' | 'partner';

export interface TaskGroupCount {
  key: string;
  label: string;
  count: number;
}

// ============================================================================
// Session Attendance Types (MED-93 E8-11)
// ============================================================================

/**
 * Attendance status for marking attendance
 */
export type AttendanceStatus = 'pending' | 'attended' | 'partial' | 'absent';

/**
 * Absent reason for attendance marking
 */
export type AbsentReason = 'business_conflict' | 'personal_conflict' | 'no_notice';

/**
 * Individual participant attendance input for marking
 */
export interface ParticipantAttendanceInput {
  user_id: string;
  status: 'attended' | 'partial' | 'absent';
  absent_reason?: AbsentReason | null;
  notes?: string | null;
}

/**
 * Participant with attendance info for modal display
 */
export interface SessionParticipant {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  attendance_id: string | null;
  current_status: AttendanceStatus;
  current_absent_reason: AbsentReason | null;
  current_notes: string | null;
  /** MED-493: Participant enrollment status */
  participant_status?: string;
  /** MED-493: When the participant was withdrawn */
  withdrawn_at?: string | null;
}

/**
 * Session participants response (for attendance modal)
 */
export interface SessionParticipantsResponse {
  session_id: string;
  session_title: string;
  session_number: number | null;
  session_date: string;
  group_id: string;
  group_name: string;
  participants: SessionParticipant[];
}

/**
 * Attendance summary for a session
 */
export interface AttendanceSummary {
  total: number;
  attended: number;
  partial: number;
  absent: number;
  pending: number;
  percentage: number;
}

/**
 * Attendance submission info for view mode
 */
export interface AttendanceSubmissionInfo {
  submitted_by_name: string;
  submitted_by_email: string;
  /** Original submission time, preserved through edits. */
  submitted_at: string;
  // MED-1078 (E1.22): state-aware status-line data for the Live Session Delivery
  // read-only-with-override CM attendance view (no schema change). The role label
  // (is_peer_led) lives on SessionAttendanceDetail since it is needed pre-submission.
  /** True when the original submitter was the session's facilitator; false drives
   *  the "Recorded by {CM name}" fallback copy (AC3). */
  submitted_by_facilitator: boolean;
  /** Latest editor + time, populated only once edited after the original
   *  submission (recorded_at > attendance_marked_at). Null otherwise. */
  last_edited_by_name: string | null;
  last_edited_at: string | null;
}

/**
 * Session with full attendance details for CM view/edit
 */
export interface SessionAttendanceDetail {
  session_id: string;
  session_title: string;
  session_number: number | null;
  session_date: string;
  group_id: string;
  group_name: string;
  cohort_id: string;
  cohort_name: string;
  /** MED-1078 (E1.22): peer-led vs coach-led role label for the LSD status line.
   *  Top-level (not inside submission) because the "Pending {role} submission"
   *  copy needs it before any attendance is recorded. */
  is_peer_led: boolean;
  submission: AttendanceSubmissionInfo | null;
  summary: AttendanceSummary;
  participants: SessionParticipant[];
}

/**
 * Mark attendance request
 */
export interface MarkAttendanceRequest {
  attendance: ParticipantAttendanceInput[];
  send_surveys: boolean;
}

/**
 * Mark attendance response
 */
export interface MarkAttendanceResponse {
  session_id: string;
  attendance_count: number;
  attended_count: number;
  partial_count: number;
  absent_count: number;
  attendance_percentage: number;
  surveys_triggered: boolean;
}

// ============================================================================
// Session Notes Types (MED-93 E8-12)
// ============================================================================

// ============================================================================
// Huddle Types (MED-268)
// ============================================================================

/**
 * Huddle type for 1:1 coaching sessions
 */
export type HuddleType = 'intro' | 'midpoint_checkin' | 'general';

/**
 * Huddle status lifecycle
 */
export type HuddleStatus = 'scheduled' | 'completed' | 'cancelled';

/**
 * Huddle attendance status
 */
export type HuddleAttendanceStatus = 'pending' | 'attended' | 'did_not_attend';

/**
 * Coach huddle record
 */
// E18-1: 1:1 coaching session slot (minimal data for list/card display)
export interface OneOnOneSessionSlot {
  id: string;
  session_number: number | null;
  status: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  timezone: string;
  video_link: string | null;
  scheduling_method: 'manual' | 'calendly' | 'calendly_fallback' | null;
  // E18-4: Post-session status indicators
  attendance_marked: boolean;
  feedback_submitted: boolean;
  // E22-5d: 1:1 session type (topic)
  one_on_one_type: OneOnOneType | null;
  // MED-1350: program week of the 1:1, or null
  target_week?: number | null;
  // MED-1350: the participant's attendance status on this row, null until marked
  attendance_status?: string | null;
  // MED-1350: the row's own coach. A check-in takes the group coach, so it can
  // differ from the participant's 1:1 coach.
  coach_id?: string | null;
  coach_name?: string | null;
  // MED-1350: the row's own group (sessions.group_id), null for most 1:1 rows
  group_id?: string | null;
}

// E18-2: Coach eligible for 1:1 assignment
export interface EligibleOneOnOneCoach {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  job_title: string | null;
  // MED-1355: the coach profile to open to add a Medley email.
  coach_profile_id?: string | null;
  // MED-1355: false when the coach has no Medley email. The 1:1 assign
  // refuses such a coach. Absent from an API older than this field.
  has_medley_email?: boolean;
}

// E18-2: Cohort participant not yet enrolled in 1:1 coaching
export interface UnenrolledOneOnOneParticipant {
  cohort_participant_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  job_title: string | null;
  group_name: string | null;
}

// E18-1: Participant row for the 1:1 Coaching section
export interface OneOnOneParticipant {
  cohort_participant_id: string;
  /**
   * MED-1350: cohort_participants.status. A withdrawn participant keeps the
   * 1:1 rows (history), and the list labels the row. The API sorts withdrawn
   * rows last.
   */
  enrollment_status?: 'pending' | 'active' | 'withdrawn';
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  job_title: string | null;
  group_id: string | null;
  group_name: string | null;
  // Coach fields — null until E18-2 (coach assignment)
  coach_id: string | null;
  coach_name: string | null;
  coach_avatar_url: string | null;
  // E18-3: Coach's default meeting link (zoom_link, teams_link, or google_meet_link from coach_profiles)
  coach_meeting_link: string | null;
  sessions: OneOnOneSessionSlot[];
  completed_count: number;
  total_session_count: number;
}

export interface CoachHuddle {
  id: string;
  cohort_id: string;
  group_id: string;
  coach_id: string;
  participant_id: string;
  cohort_participant_id: string | null;
  participant_user_id: string;
  participant_name: string;
  participant_email: string;
  participant_title: string | null;
  huddle_type: HuddleType;
  title: string;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  video_link: string | null;
  status: HuddleStatus;
  attendance_status: HuddleAttendanceStatus;
  attendance_notes: string | null;
  feedback_notes: string | null;
  google_calendar_event_id: string | null;
  invites_sent: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Request to create a huddle
 */
export interface CreateHuddleRequest {
  group_id: string;
  participant_id: string;
  huddle_type: HuddleType;
  title: string;
  scheduled_start: string;
  duration_minutes: number;
  video_link?: string;
  timezone?: string;
  send_invite: boolean;
}

/**
 * Request to update a huddle
 */
export interface UpdateHuddleRequest {
  huddle_type?: HuddleType;
  title?: string;
  participant_id?: string;
  scheduled_start?: string;
  duration_minutes?: number;
  video_link?: string | null;
  timezone?: string;
}

/**
 * Request to reschedule a huddle
 */
export interface RescheduleHuddleRequest {
  scheduled_start: string;
  duration_minutes?: number;
  timezone?: string;
  send_invite: boolean;
}

/**
 * Request to mark huddle attendance
 */
export interface MarkHuddleAttendanceRequest {
  status: 'attended' | 'did_not_attend';
  notes?: string;
}

/**
 * Request to save huddle feedback
 */
export interface SaveHuddleFeedbackRequest {
  notes: string;
}

// ============================================================================
// Session Notes Types (MED-93 E8-12)
// ============================================================================

/**
 * Session note record
 */
export interface SessionNote {
  id: string;
  session_id: string;
  content: string;
  created_by: string;
  created_at: string;
  is_deleted: boolean;
}

/**
 * Session note with author details
 */
export interface SessionNoteWithAuthor extends SessionNote {
  author_name: string;
  author_email: string;
}

/**
 * Session notes list response
 */
export interface SessionNotesResponse {
  session_id: string;
  session_title: string;
  session_number: number | null;
  session_date: string;
  group_id: string;
  group_name: string;
  notes: SessionNoteWithAuthor[];
}

// ============================================================================
// Dashboard Types (MED-290)
// ============================================================================

export interface DashboardActiveCohort {
  id: string;
  name: string;
  status: string;
  partner_name: string;
  partner_code: string | null;
  completed_sessions: number;
  total_sessions: number;
  health_status: OverallHealthStatus;
}

export interface DashboardUpcomingSession {
  cohort_id: string;
  cohort_name: string;
  partner_name: string;
  session_number: number;
  topic_name: string | null;
  week_of: string;
  group_count: number;
}

export interface DashboardAttentionItem {
  cohort_id: string;
  cohort_name: string;
  alert: HealthAlert;
}

export interface DashboardAttentionSection {
  risk: {
    count: number;
    items: DashboardAttentionItem[];
  };
  monitor: {
    count: number;
    items: DashboardAttentionItem[];
  };
}

export interface DashboardCoachAwaitingContract {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  contract_status: string;
  announced_at: string | null;
}

export interface DashboardCohortCoachGroup {
  cohort_id: string;
  cohort_name: string;
  coaches: DashboardCoachAwaitingContract[];
}

// ============================================================================
// Data Library Types
// ============================================================================

export interface DataLibraryParticipantFlag {
  id: string;
  flag_category: ParticipantFlagCategory;
  flag_reason: string;
  status: string;
  session_number: number | null;
  created_at: string;
}

export interface DataLibraryParticipant {
  id: string;
  user_id: string;
  cohort_id: string;
  first_name: string;
  last_name: string;
  email: string;
  preferred_name: string | null;
  location: string | null;
  status: ParticipantStatus;
  personality_type: string | null;
  role: string | null;
  business_unit: string | null;
  manager: string | null;
  level: string | null;
  dnm: string | null;
  enrolled_at: string;
  cohort_name: string;
  cohort_health: string | null;
  partner_name: string | null;
  group_id: string | null;
  group_name: string | null;
  coach_id: string | null;
  coach_name: string | null;
  timeslot: string | null;
  sessions_attended: number;
  // MED-1266: total_sessions is the recorded-outcome count (marked); attendance_population
  // is marked + pending (the coverage denominator).
  total_sessions: number;
  attendance_percentage: number | null;
  attendance_population: number;
  flags: DataLibraryParticipantFlag[];
  has_open_flag: boolean;
  flag_severity: 'high' | 'medium' | 'low' | null;
  midpoint_completed: boolean;
  endpoint_completed: boolean;
  satisfaction_score: string | null;
  mid_satisfaction: string | null;
  end_satisfaction: string | null;
  mid_coach_satisfaction: string | null;
  end_coach_satisfaction: string | null;
  avg_engagement: number | string | null;
  sessions_attendance: Array<{
    session_number: number;
    status: 'attended' | 'partial' | 'absent' | 'excused' | 'pending' | null;
  }>;
  sessions_engagement: Array<{
    session_number: number;
    score: number | null;
    topic: string | null;
  }>;
  last_email_type: string | null;
  last_email_date: string | null;
  coaching_experience: string | null;
  coaching_value: string | null;
}

export interface DataLibraryFilterOptions {
  groups: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
  departments: string[];
  locations: string[];
}

export interface DataLibraryCohortSummary {
  id: string;
  name: string;
  status: string;
  participant_count: number;
  start_date: string | null;
  end_date: string | null;
}

export interface DataLibraryCohortGroup {
  partner_id: string;
  partner_name: string;
  program_name: string;
  cohorts: DataLibraryCohortSummary[];
}

// ============================================================================
// Data Library Survey Response Types
// ============================================================================

export interface DataLibrarySurveyQuestion {
  question_id: string;
  question_text: string;
  // MED-888: optional short label for CM-facing column headers (full text on hover).
  // When present, column rendering should use this and put `question_text` in a Tooltip.
  short_label?: string;
  question_type: 'scale' | 'text' | 'choices' | 'yesNo';
  display_order: number;
}

export interface DataLibrarySurveyRow {
  submission_id: string;
  participant_id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  group_id: string | null;
  group_name: string | null;
  coach_name: string | null;
  coach_email: string | null;
  attendance_rate: number | null;
  // MED-1266: marking coverage — optional; not all survey-row API paths carry it yet.
  attendance_marked?: number;
  attendance_population?: number;
  form_type: string;
  survey_stage: 'Midpoint' | 'Endpoint';
  cohort_id: string;
  cohort_name: string;
  cohort_health: string | null;
  has_open_flag: boolean;
  flags: Array<{ id: string; flag_category: string; flag_reason: string; status: string; session_number: number | null }>;
  has_absences: boolean;
  /** MDF-212: average engagement over attended, held sessions */
  avg_engagement: number | null;
  overall_satisfaction: string | null;
  coach_satisfaction: string | null;
  responses: Record<string, string | string[]>;
}

export interface DataLibrarySurveyFilterOptions {
  groups: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
}

// ============================================================================
// Data Library Coach Survey Response Types
// ============================================================================

export interface DataLibraryCoachSurveyRow {
  submission_id: string;
  coach_user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  submitted_at: string;
  cohort_id: string;
  cohort_name: string;
  cohort_health: string | null;
  overall_satisfaction: string | null;
  /** MED-668: Survey stage label (Midpoint/Endpoint) for mid-vs-end grouping */
  survey_stage: string;
  responses: Record<string, string | string[]>;
}

export interface DataLibraryCoachSurveyFilterOptions {
  cohorts: Array<{ value: string; label: string }>;
}

// ============================================================================
// MED-668: Data Library Coach Post Session Types
// ============================================================================

export interface CoachPostSessionAttendee {
  participant_name: string;
  status: 'attended' | 'partial' | 'excused_business' | 'excused_personal' | 'unexcused' | null;
}

export interface CoachPostSessionFlag {
  participant_name: string;
  concern_type: string;
  notes: string | null;
}

export interface DataLibraryCoachPostSessionRow {
  session_id: string;
  cohort_id: string;
  cohort_name: string;
  coach_name: string;
  coach_email: string;
  group_id: string | null;
  group_name: string | null;
  session_number: number | null;
  session_topic: string | null;
  session_date: string;
  attendance: CoachPostSessionAttendee[];
  absence_reasons: string;
  coach_notes: string | null;
  /** MDF-212: the session attendance rate (integer percent) and mean engagement rating */
  attendance_rate: number | null;
  avg_engagement: number | null;
  engagement: Array<{ participant_name: string; rating: number }>;
  concern_flags: CoachPostSessionFlag[];
  feedback_submitted: boolean;
  date_submitted: string | null;
  responses: Record<string, string | string[]>;
}

export interface DataLibraryCoachPostSessionFilterOptions {
  groups: Array<{ value: string; label: string }>;
  cohorts: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
  sessionNumbers: Array<{ value: string; label: string }>;
}

// ============================================================================
// Data Library Session Feedback Types (participant × session grain)
// ============================================================================

export interface DataLibrarySessionFeedbackRow {
  submission_id: string;
  participant_id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  session_number: number;
  session_topic: string;
  session_date: string;
  coach_name: string | null;
  date_submitted: string;
  cohort_id: string;
  cohort_name: string;
  cohort_health: string | null;
  group_id: string | null;
  group_name: string | null;
  has_open_flag: boolean;
  has_absences: boolean;
  /** MDF-212: the participant attendance rate (integer percent) and average engagement */
  attendance_rate: number | null;
  avg_engagement: number | null;
  overall_satisfaction: string | null;
  responses: Record<string, string | string[]>;
}

export interface DataLibrarySessionFeedbackFilterOptions {
  groups: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
  sessionNumbers: Array<{ value: string; label: string }>;
}

// ============================================================================
// Data Library Attendance Types (session × participant grain)
// ============================================================================

export type AttendanceDisplayStatus =
  | 'attended'
  | 'partial'
  | 'excused_business'
  | 'excused_personal'
  | 'unexcused'
  // MED-1267 (AC21) — durable marker that the participant was withdrawn at the
  // session; hidden from every roster but surfaced here in the Data Library
  // attendance table / export so CMs/Admins can discover it.
  | 'withdrawn'
  | null;

export interface DataLibraryAttendanceRow {
  row_id: string;
  participant_id: string;
  participant_email: string;
  participant_first_name: string;
  participant_last_name: string;
  participant_full_name: string;
  session_number: number;
  session_topic: string;
  session_date: string;
  cohort_id: string;
  cohort_name: string;
  cohort_health: string | null;
  cohort_status: string;
  coach_name: string | null;
  group_id: string | null;
  group_name: string | null;
  participant_status: 'pending' | 'active' | 'withdrawn';
  attendance_status: AttendanceDisplayStatus;
  coach_notes: string | null;
  overall_attendance_rate: number | null;
  session_attendance_rate: number | null;
  // MED-1266: marking coverage for each rate — marked of population.
  overall_attendance_marked: number;
  overall_attendance_population: number;
  session_attendance_marked: number;
  session_attendance_population: number;
  total_unexcused: number;
  total_missed: number;
  business_unit: string | null;
  department: string | null;
  role: string | null;
  location: string | null;
  repeat_medley: string | null;
  engagement_rating: number | null;
  /** MDF-212: the participant average engagement, as on the Participants tab */
  avg_engagement: number | null;
  has_open_flag: boolean;
  flags: Array<{ id: string; flag_category: string; flag_reason: string; session_number: number | null }>;
}

export interface DataLibraryAttendanceFilterOptions {
  groups: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
  sessionNumbers: Array<{ value: string; label: string }>;
  departments: string[];
  locations: string[];
  businessUnits: string[];
}

// ============================================================================
// Data Library Session Summary Types (one row per session)
// ============================================================================

export interface DataLibrarySessionAttendee {
  participant_id: string | null;
  participant_email: string;
  participant_name: string;
  status: AttendanceDisplayStatus;
}

export interface DataLibrarySessionNote {
  id: string;
  content: string;
  author_name: string;
  created_at: string;
}

/**
 * MED-1200 (C2C) — one anonymized per-participant airtime pill. Sourced from the
 * shared B6/B7 airtime output; only these anonymized fields cross the wire —
 * never a real name (AC2). `dominates` is B10's persisted one-participant-
 * dominating verdict (max(2× even share, 25%)), so the pill color matches B10
 * feedback and the C2B pill exactly (AC3).
 */
export interface DataLibrarySessionAirtimeEntry {
  label: string; // "Participant N"
  seconds: number;
  share_of_participants_pct: number;
  dominates: boolean;
}

export interface DataLibrarySessionRow {
  session_id: string;
  cohort_id: string;
  cohort_name: string;
  cohort_health: string | null;
  session_number: number | null;
  session_topic: string | null;
  group_id: string | null;
  group_name: string | null;
  coach_name: string | null;
  session_status: string;
  // MED-1266: nullable when no outcomes recorded; coverage travels alongside.
  session_attendance_rate: number | null;
  session_attendance_marked: number;
  session_attendance_population: number;
  health_nudges: Array<{ title: string; severity: string; stat_value: string; stat_label: string }>;
  is_flagged: boolean;
  attendance: DataLibrarySessionAttendee[];
  engagement: Array<{ participant_name: string; rating: number }>;
  /**
   * MED-1200 (C2C) — per-participant airtime, tri-state:
   *   null  → cohort not live-session-delivery + group-coaching (AC6, "not applicable")
   *   []    → qualifying cohort but no usable diarized transcript (AC5, "not available")
   *   [...] → qualifying cohort with computed shares (AC1/AC3)
   */
  airtime: DataLibrarySessionAirtimeEntry[] | null;
  session_date: string;
  total_attended: number;
  total_participants: number;
  internal_notes: DataLibrarySessionNote[];
}

export interface DataLibrarySessionFilterOptions {
  groups: Array<{ value: string; label: string }>;
  cohorts: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
  sessionMonths: Array<{ value: string; label: string }>;
  sessionNumbers: Array<{ value: string; label: string }>;
}

/** MDF-41: column id to its average over all matching rows; null when every value is blank. */
export type ColumnAverages = Record<string, number | null>;

// ============================================================================
// MDF-253: Data Library 1:1s tab (one row per 1:1 session)
// ============================================================================

export type OneOnOneReportStatus =
  | 'completed'
  | 'no_show'
  | 'canceled'
  | 'needs_attendance'
  | 'scheduled'
  | 'not_scheduled'
  | 'withdrawn';

export interface DataLibraryOneOnOneRow {
  session_id: string;
  cohort_id: string;
  cohort_name: string;
  participant_id: string | null;
  participant_name: string | null;
  participant_email: string | null;
  coach_id: string | null;
  coach_name: string | null;
  group_id: string | null;
  group_name: string | null;
  one_on_one_type: OneOnOneType | null;
  scheduled_start: string | null;
  status: OneOnOneReportStatus;
  absent_reason: string | null;
  booked_via: 'Calendly' | 'Google invite' | 'Manual' | null;
  attendance_notes: string | null;
  session_notes: string | null;
  engagement_rating: number | null;
  feedback: Array<{ label: string; answer: string }>;
}

export interface DataLibraryOneOnOneSummary {
  total: number;
  completed: number;
  scheduled: number;
  needs_attendance: number;
  not_scheduled: number;
  canceled: number;
  no_show: number;
  withdrawn: number;
  completion_rate: number | null;
}

export interface DataLibraryOneOnOneFilterOptions {
  cohorts: Array<{ value: string; label: string }>;
  coaches: Array<{ value: string; label: string }>;
  types: Array<{ value: string; label: string }>;
}
